import { useEffect, useRef, useState } from 'react';

// Plays a room's recent lines one at a time, as if it were talking now: the
// newest `max` stay on screen and the oldest steps out. Runs only while
// `active` (on screen, tab visible). A new `resetKey` (another room) starts
// over. prefers-reduced-motion: the first `max` lines, still.
export const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

export default function useTrickle(lines, { max = 3, seed = 1, every = [2600, 4200], delay = 0, active = true, resetKey = '' } = {}) {
  const [items, setItems] = useState([]);
  const pos = useRef({ i: 0, seq: 0 });
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const still = useRef(reducedMotion());

  useEffect(() => {
    const src = linesRef.current || [];
    pos.current = { i: 0, seq: 0 };
    const n = Math.min(still.current ? max : seed, src.length);
    const first = [];
    for (let k = 0; k < n; k += 1) {
      first.push({ k: pos.current.seq++, l: src[pos.current.i++ % src.length], leaving: false, fresh: false });
    }
    setItems(first);
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const src = linesRef.current || [];
    if (!active || still.current || src.length < 2) return undefined;
    let alive = true;
    let t = null;
    const drops = new Set();
    const step = () => {
      const now = linesRef.current || [];
      if (!now.length) return;
      const next = { k: pos.current.seq++, l: now[pos.current.i++ % now.length], leaving: false, fresh: true };
      setItems((prev) => {
        const live = prev.filter(x => !x.leaving);
        const extra = live.length + 1 - max;
        const out = extra > 0
          ? prev.map(x => (live.indexOf(x) > -1 && live.indexOf(x) < extra ? { ...x, leaving: true } : x))
          : prev.slice();
        out.push(next);
        return out;
      });
      const d = setTimeout(() => {
        drops.delete(d);
        if (alive) setItems(prev => prev.filter(x => !x.leaving));
      }, 320);
      drops.add(d);
    };
    const loop = (first) => {
      const [a, b] = every;
      t = setTimeout(() => {
        if (!alive) return;
        step();
        loop(false);
      }, (first ? delay : 0) + a + Math.random() * (b - a));
    };
    loop(true);
    return () => {
      alive = false;
      clearTimeout(t);
      drops.forEach(clearTimeout);
    };
  }, [active, resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return items;
}
